import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

export const reviveTip = (r) => `Revived by ${r.by?.name} on ${fmtDateTime(r.at)}: ${r.reason} (was cancelled by ${r.cancelled_by?.name || "unknown"} on ${fmtDateTime(r.cancelled_at)}: ${r.cancel_reason || "-"})`;

export const RevivedBadge = ({ o, testid }) => (o.revived && o.status !== "cancelled"
  ? <span className="ml-1 rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-sans font-semibold text-teal-800" title={reviveTip(o.revived)} data-testid={testid || "lm-revived-badge"}>Revived</span> : null);

export const Warnings = ({ list, testid }) => (list?.length ? <ul className="list-disc pl-4 text-xs text-amber-800" data-testid={testid}>{list.map((w) => <li key={w}>{w}</li>)}</ul> : null);

// Admin: revive one cancelled LM back to its status before cancellation
export function ReviveDialog({ order, onClose, onDone }) {
  const { data: pv } = useFetch(`/orders/${order.id}/revive-preview`);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!pv) return null;
  const go = async () => {
    if (reason.trim().length < 3) return toast.error("Enter the reason for reviving (at least 3 characters)");
    setBusy(true);
    try { await api.post(`/orders/${order.id}/revive`, { reason }); toast.success(`${order.order_no} revived to ${pv.restore_to.replace(/_/g, " ")}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[95vw]" data-testid="revive-dialog">
        <DialogHeader><DialogTitle>Revive LM · {order.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-1 text-sm" data-testid="revive-info">
          <p>Cancelled by <b>{pv.cancelled_by?.name || "—"}</b> on {fmtDateTime(pv.cancelled_at)}</p>
          <p>Cancellation reason: {pv.cancel_reason || "—"}</p>
          <p className="flex items-center gap-2">Will be restored to: <StatusBadge status={pv.restore_to} testid="revive-target" /></p>
          {pv.fallback && <p className="text-xs text-amber-800" data-testid="revive-fallback">No cancellation history found - the LM will be restored to draft.</p>}
        </div>
        <Warnings list={pv.warnings} testid="revive-warnings" />
        <div><Label>Reason for reviving *</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="revive-reason" /></div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="revive-cancel">Cancel</Button>
          <Button disabled={busy} onClick={go} data-testid="revive-submit">Revive LM</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
